/* eslint-disable check-preloaders/no-promise-all */
import { getLogger } from '@confluence/logger';

import { defer } from './defer.ts';
import { startPreload } from './deferredStart.ts';
import {
	LOADING_TIMEOUT,
	REACT_LAZY_ID_PREFIX,
	getComponentName,
	reportLoadableTimeouts,
} from './loadableGlobals.ts';

const logger = getLogger('Loadable');

let pendingPreloadInvokes: { [nameOrId: string]: () => void } = {};
let resolveMap: {
	[nameOrId: string]: {
		resolve: Function;
		promise: Promise<any>;
		cancel: Function;
	};
} = {};
const pendingImmediatePreloadInvokes = new Set<string>();

function enqueueLoadable(id: string) {
	if (resolveMap[id]) return;
	const { resolve, promise, cancel } = defer(LOADING_TIMEOUT, () => {
		logger.error`Loadable initialization timed out: ${id}`;
		(window.__LOADABLE_TIMEOUTS__ ||= []).push(id);
	});
	resolveMap[id] = { resolve, promise, cancel };

	// Enqueue can happen before or after a loadable is discovered.
	// If enqueue happens before, we need to invoke the preload immediately when we discover the loadable.
	pendingImmediatePreloadInvokes.add(id);
	// If enqueue happens after, we need to call preload() on previously discovered loadable.
	pendingPreloadInvokes[id]?.();
}

function initializeLoadable() {
	// enqueueLoadable is safe to call multiple times.
	// Skip empty loadables - they have no content and should not block preloading
	const emptyLoadables = window.__EMPTY_LOADABLE__ || [];
	for (const nameOrId of window.__LOADABLE__ || []) {
		// Check if this is an empty loadable by extracting component name
		const componentName = getComponentName(nameOrId);
		// preloadLoadable decides whether a React.lazy id waits
		if (!emptyLoadables.includes(componentName) && !nameOrId.startsWith(REACT_LAZY_ID_PREFIX)) {
			enqueueLoadable(nameOrId);
		}
	}
}

export async function preloadLoadable(): Promise<any> {
	initializeLoadable();

	// Re-check for empty loadables that may have been detected after initializeLoadable() ran
	// This catches empty loadables whose dequeue events arrived late
	const emptyLoadables = window.__EMPTY_LOADABLE__ || [];
	const emptyComponentNames = new Set(emptyLoadables);

	// A React.lazy id waits only when the module that calls `lazy()` has already run. Atlaspack runs
	// the other modules when they are first used, which can be during hydration, so waiting for them
	// would hold hydration until the timeout. Their components suspend once during hydration instead,
	// and the server has already requested their split files.
	for (const id of window.__LOADABLE__ || []) {
		if (
			id.startsWith(REACT_LAZY_ID_PREFIX) &&
			id in pendingPreloadInvokes &&
			!emptyComponentNames.has(id)
		) {
			enqueueLoadable(id);
		}
	}

	for (const id in resolveMap) {
		if (!resolveMap[id]) {
			continue;
		}
		const componentName = getComponentName(id);
		if (emptyComponentNames.has(componentName)) {
			resolveMap[id].cancel();
			delete resolveMap[id];
		}
	}

	try {
		await Promise.all(Object.values(resolveMap).map(({ promise }) => promise));
		reportLoadableTimeouts();
	} finally {
		// Note here we don't clear resolveMap logic is using this to avoid load loadable twice.
		pendingPreloadInvokes = {};
		pendingImmediatePreloadInvokes.clear();
	}
}

export function preloadIfNeeded(
	id: string | undefined,
	name: string | undefined,
	preload: () => Promise<any>,
): void {
	initializeLoadable();
	const invokePreload = () => {
		if (id) delete pendingPreloadInvokes[id];
		if (name) delete pendingPreloadInvokes[name];

		if ((id && id in resolveMap) || (name && name in resolveMap)) {
			const resolve = () => {
				// Note that due to the _mustPreloadWhenRenderingSPA logic the loadable may appear
				// in the list of loadables both under id and under name. If we have 2 entries for
				// the loadable, we should resolve both.
				if (id && resolveMap[id]) resolveMap[id].resolve();
				if (name && resolveMap[name]) resolveMap[name].resolve();
			};
			const start = () => {
				const startTime = performance.now();
				void preload().then(
					() => {
						logger.debug`SSR Preload: ${name || id} in ${Math.floor(performance.now() - startTime)}ms`;
						resolve();
					},
					(e) => {
						logger.error(e);
						resolve();
					},
				);
			};
			startPreload(start);
		}
	};

	if (
		(id && pendingImmediatePreloadInvokes.has(id)) ||
		(name && pendingImmediatePreloadInvokes.has(name))
	) {
		invokePreload();
	} else {
		// __LOADABLE__ is not available when we execute code splits early than DOMContentLoaded
		// As soon as loadable are imported preloadIfNeeded gonna run because they are defined at module level.
		// In these cases we don't have a resolveMap to work with.
		// So we are saving the ids and invoke later when we get signal from SSR either stream or final __LOADABLE__
		if (id) pendingPreloadInvokes[id] = invokePreload;
		if (name) pendingPreloadInvokes[name] = invokePreload;
	}
}

export function _resetPreloaderV1OnlyForTest(): void {
	resolveMap = {};
	pendingPreloadInvokes = {};
	pendingImmediatePreloadInvokes.clear();
}
